import { Link } from "react-router-dom";
import { ArrowRight, Check, ClipboardCheck, Landmark, Building2, FileText, Database } from "lucide-react";
import { GREEN, BLUE, ORANGE, SectionTitle, FlowArrow, Wordmark } from "./shared";

export function ProblemaSection() {
  return (
    <section className="py-20 lg:py-24" data-testid="section-problema">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="lp-reveal">
          <SectionTitle
            kicker="Il problema"
            title="L'ESG non dovrebbe essere un altro problema da gestire."
            sub="Questionari diversi, richieste delle banche, dati HR, consumi, documenti, VSME, clienti e fornitori. ESG-PMI riunisce tutto in un unico percorso guidato." />
        </div>
        <div className="mt-12 grid md:grid-cols-[1fr_auto_1fr] gap-6 items-center max-w-4xl">
          <div className="lp-reveal rounded-xl border border-slate-200 bg-slate-50 p-6">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-4">Prima</p>
            <div className="flex flex-wrap gap-2">
              {["Excel", "PDF", "Email", "Questionari", "Dati sparsi"].map((t) => (
                <span key={t} className="text-xs font-medium bg-white border border-slate-300 rounded-full px-3 py-1.5 text-slate-600 line-through decoration-slate-400">{t}</span>
              ))}
            </div>
          </div>
          <div className="flex md:flex-col items-center justify-center gap-1" aria-hidden="true">
            <ArrowRight className="w-6 h-6 hidden md:block" style={{ color: ORANGE }} />
            <ArrowRight className="w-6 h-6 md:hidden rotate-90" style={{ color: ORANGE }} />
          </div>
          <div className="lp-reveal rounded-xl border-2 p-6 text-center" style={{ borderColor: GREEN, background: "#F2F9F5" }}>
            <Wordmark compact />
            <p className="mt-3 text-sm font-semibold" style={{ color: GREEN }}>Un'unica base dati ESG</p>
            <p className="text-xs text-slate-600 mt-1">organizzata, verificabile, riutilizzabile</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export function SoluzioneSection() {
  return (
    <section id="soluzione" className="py-20 lg:py-24 scroll-mt-20" style={{ background: "#FAFBFA" }} data-testid="section-soluzione">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="lp-reveal text-center max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
            Una sola raccolta dati. <span style={{ color: GREEN }}>Più utilizzi.</span>
          </h2>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {[
            { icon: ClipboardCheck, t: "VSME", d: "Rendicontazione volontaria europea per le PMI, guidata passo dopo passo.", c: GREEN },
            { icon: Landmark, t: "PMI-Banche", d: "Dialogo ESG con il sistema bancario secondo il Documento MEF PMI-Banche.", c: BLUE },
            { icon: Building2, t: "Clienti e filiere", d: "Informazioni ESG già organizzate e riutilizzabili per clienti e supply chain.", c: "#1E9E76" },
          ].map(({ icon: Icon, t, d, c }) => (
            <div key={t} className="lp-reveal rounded-2xl border border-slate-200 bg-white p-7 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <span className="inline-flex w-11 h-11 rounded-lg items-center justify-center" style={{ background: `${c}14`, color: c }}>
                <Icon className="w-5 h-5" aria-hidden="true" />
              </span>
              <h3 className="mt-4 text-lg font-bold" style={{ fontFamily: "Manrope, sans-serif" }}>{t}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function VsmeSection() {
  return (
    <section id="vsme" className="py-20 lg:py-24 scroll-mt-20" data-testid="section-vsme">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div className="lp-reveal">
          <SectionTitle
            kicker="Standard VSME"
            title="Il tuo VSME, passo dopo passo."
            sub="ESG-PMI traduce i requisiti VSME in domande semplici e comprensibili. Ogni risposta aggiorna il tuo stato di rendicontazione in tempo reale." />
          <Link to="/vsme"
            className="mt-8 inline-flex items-center gap-2 text-sm font-bold px-6 py-3 rounded-md text-white transition-transform hover:-translate-y-0.5"
            style={{ background: GREEN }} data-testid="cta-vsme">
            Scopri il VSME <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </div>
        <div className="lp-reveal rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="vsme-flow">
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Domanda semplice</p>
            <p className="mt-1 text-sm font-semibold text-slate-900">"Utilizzate energia elettrica?"</p>
            <span className="mt-2 inline-block text-xs font-bold px-3 py-1 rounded-full text-white" style={{ background: GREEN }}>Sì</span>
          </div>
          <FlowArrow />
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Dato</p>
            <p className="mt-1 text-sm font-semibold text-slate-900">"Quanto avete consumato?" → 10.000 kWh</p>
          </div>
          <FlowArrow />
          <div className="rounded-lg border-2 p-4 flex items-center justify-between" style={{ borderColor: GREEN, background: "#F2F9F5" }}>
            <div>
              <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: GREEN }}>VSME</p>
              <p className="text-sm font-bold" style={{ color: GREEN }}>VSME B3 aggiornato</p>
            </div>
            <Check className="w-5 h-5" style={{ color: GREEN }} aria-hidden="true" />
          </div>
          <FlowArrow />
          <div className="rounded-lg bg-slate-900 p-4 flex items-center gap-2">
            <FileText className="w-4 h-4 text-white" aria-hidden="true" />
            <p className="text-sm font-semibold text-white">Report pronto per banche e clienti</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export function PmiBancheSection() {
  return (
    <section id="pmi-banche" className="py-20 lg:py-24 scroll-mt-20" style={{ background: "#FAFBFA" }} data-testid="section-pmi-banche">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div className="lp-reveal order-2 lg:order-1 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg p-4 text-center" style={{ background: "#E7F4EE" }}>
              <ClipboardCheck className="w-5 h-5 mx-auto" style={{ color: GREEN }} aria-hidden="true" />
              <p className="mt-2 text-sm font-bold" style={{ color: GREEN }}>VSME</p>
            </div>
            <div className="rounded-lg p-4 text-center" style={{ background: "#E9EFF6" }}>
              <Landmark className="w-5 h-5 mx-auto" style={{ color: BLUE }} aria-hidden="true" />
              <p className="mt-2 text-sm font-bold" style={{ color: BLUE }}>MEF PMI-Banche</p>
            </div>
          </div>
          <FlowArrow />
          <div className="rounded-lg border-2 border-dashed p-4 text-center" style={{ borderColor: ORANGE }}>
            <Database className="w-5 h-5 mx-auto" style={{ color: ORANGE }} aria-hidden="true" />
            <p className="mt-2 text-sm font-bold text-slate-900">Stessa base dati</p>
            <p className="text-xs text-slate-600 mt-1">il dato inserito una volta alimenta entrambi i framework</p>
          </div>
        </div>
        <div className="lp-reveal order-1 lg:order-2">
          <SectionTitle
            kicker="Dialogo bancario"
            title="Parla la stessa lingua della tua banca."
            sub="ESG-PMI organizza le informazioni previste dal Documento per il dialogo di sostenibilità tra PMI e banche, riutilizzando i dati già raccolti per il VSME." />
          <p className="mt-6 inline-block text-sm font-extrabold px-4 py-2.5 rounded-lg"
            style={{ background: "#FBF0E1", color: "#B85E0F" }} data-testid="pmi-banche-claim">
            Non compilare due volte le stesse informazioni.
          </p>
          <div>
            <Link to="/pmi-banche"
              className="mt-5 inline-flex items-center gap-2 text-sm font-bold px-6 py-3 rounded-md text-white transition-transform hover:-translate-y-0.5"
              style={{ background: BLUE }} data-testid="cta-pmi-banche">
              Scopri PMI-Banche <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export function JobtimehrSection() {
  return (
    <section id="jobtimehr" className="py-20 lg:py-24 scroll-mt-20" style={{ background: BLUE }} data-testid="section-jobtimehr">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="lp-reveal">
          <SectionTitle light
            kicker="Integrazione JOBTIMEHR"
            title="I dati HR sono già lì."
            sub="Se utilizzi JOBTIMEHR, una parte importante dei dati Social può essere acquisita automaticamente: presenze, contratti, formazione e ore lavorate diventano KPI ESG senza reinserimenti." />
        </div>
        <div className="mt-12 grid lg:grid-cols-[1fr_auto_1fr_auto_1fr] gap-4 items-stretch">
          <div className="lp-reveal rounded-xl bg-white/10 border border-white/20 p-5">
            <p className="text-xs font-bold uppercase tracking-wider text-white/70 mb-3">JOBTIMEHR</p>
            <div className="flex flex-wrap gap-2">
              {["Presenze", "Contratti", "Formazione", "Ore lavorate", "Workforce"].map((t) => (
                <span key={t} className="text-xs font-medium text-white bg-white/10 border border-white/20 rounded-full px-3 py-1.5">{t}</span>
              ))}
            </div>
          </div>
          <div className="hidden lg:flex items-center" aria-hidden="true">
            <ArrowRight className="w-6 h-6" style={{ color: ORANGE }} />
          </div>
          <div className="lp-reveal rounded-xl p-5 border-2" style={{ background: "rgba(242,140,40,0.12)", borderColor: ORANGE }}>
            <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: "#F5A94F" }}>ESG-PMI</p>
            <p className="text-sm font-bold text-white">Social KPI</p>
            <p className="text-xs text-white/70 mt-1">calcolati e verificabili</p>
          </div>
          <div className="hidden lg:flex items-center" aria-hidden="true">
            <ArrowRight className="w-6 h-6" style={{ color: ORANGE }} />
          </div>
          <div className="lp-reveal rounded-xl bg-white p-5">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Output</p>
            <div className="flex flex-wrap gap-2">
              <span className="text-xs font-bold rounded-full px-3 py-1.5" style={{ background: "#E7F4EE", color: GREEN }}>VSME</span>
              <span className="text-xs font-bold rounded-full px-3 py-1.5" style={{ background: "#E9EFF6", color: BLUE }}>PMI-Banche</span>
            </div>
          </div>
        </div>
        <div className="lp-reveal mt-10 flex flex-wrap items-center gap-5">
          <p className="text-base md:text-lg font-extrabold text-white" data-testid="jobtimehr-claim">
            Non inserire due volte un dato che <span style={{ color: ORANGE }}>JOBTIMEHR</span> conosce già.
          </p>
          <Link to="/jobtimehr"
            className="inline-flex items-center gap-2 text-sm font-bold px-6 py-3 rounded-md transition-transform hover:-translate-y-0.5"
            style={{ background: ORANGE, color: BLUE }} data-testid="cta-jobtimehr">
            Scopri l'integrazione JOBTIMEHR <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}
